/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface IconProps {
    /**
     * A string representing the CSS class to be applied to the PowerIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the PowerIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the PowerIcon element.
     */
    width?: number | string;
}

/**
 * The `Power` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `PowerProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the PowerIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the PowerIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the PowerIcon element.
 * @returns A React element representing the `Power` component with the specified properties.
 *
 * @example
 * ```tsx
 * const PowerComponent = <Power className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Power = ({
    className = '',
    color1 = 'currentColor',
    height = 64,
    ref,
    testId = 'Power',
    width = 64
}: IconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 64 64"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M32 34c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2s2 .9 2 2v26c0 1.1-.9 2-2 2z" fill={color1} />
        <path d="M32 56.5c-13.5 0-24.5-11-24.5-24.5 0-10.6 6.8-20 16.9-23.3 1-.3 2.2.2 2.5 1.3.3 1-.2 2.2-1.3 2.5-8.4 2.8-14.1 10.6-14.1 19.5 0 11.3 9.2 20.5 20.5 20.5S52.5 43.3 52.5 32c0-8.9-5.7-16.7-14.1-19.5-1-.3-1.6-1.5-1.3-2.5.3-1 1.5-1.6 2.5-1.3C49.7 12 56.5 21.4 56.5 32c0 13.5-11 24.5-24.5 24.5z" fill={color1} />
    </svg>
);

Power.displayName = 'Power';